import { css } from '@emotion/core';
import { useState, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import _ from 'underscore';

// Selectors
import { getCourseAliases, getCourseActivities } from 'redux/selectors/course';
import { getLabelForActivity, getActivityTypeForKey } from 'redux/selectors/activity';
import { getCourseTimeLine } from 'redux/selectors/timeline';

// Schemas
import { CourseAliases, TimelineNormalized, TimelineItemTypes } from 'redux/schemas/models/course';
import { CourseActivities } from 'redux/schemas/api/course-communications';
import { ActivityKey } from 'redux/schemas/models/activity';
import { LecturePageType } from 'redux/schemas/models/lecture-page';
import { OwnerType } from 'redux/schemas/models/course-communication';
import { RteTag, RteTagTypes } from 'froala/helpers/nv-froala-constants';

// Styles
import { standardSpacing, quarterSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { textMediumFontSize } from 'styles/global_defaults/fonts';

// Components
import NvDropdown, {
  NvDropdownTextItem,
  NvDropdownButtonStyle,
  NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import { convertRelatedActivityToRteTag, createRteTagValue } from 'froala/helpers/nv-froala-tag-functions';

const styles = css`
  margin: ${standardSpacing}px 0px;

  > div {
    padding-left: 0px;
  }

  .add-tags-button-container {
    padding-top: ${quarterSpacing}px;
    padding-right: 0px;

    button {
      margin-right: ${quarterSpacing}px;
      &:last-child {
        margin-right: 0px
      }
    }
  }

  .bs4-dropdown {
    .bs4-dropdown-menu {
      .rte-tag-dropdown-item {
        font-size: ${textMediumFontSize}px;
        padding: ${quarterSpacing}px ${threeQuartersSpacing}px;
      }
    }
  }

  .rte-tag-dropdown-title {
    font-size: ${textMediumFontSize}px;
    line-height: 16px;
  }

  .page-title-xxs {
    margin: ${quarterSpacing}px ${threeQuartersSpacing}px;
  }

  .bs4-dropdown-menu {
    margin-bottom: ${standardSpacing}px;
  }
`;

// Get label for activity type dropdown for pick another date
const getDateActivityDropdownLabel = (activityKey: ActivityKey, aliases: CourseAliases): string => {
  const label = activityKey === ActivityKey.LIVE_SESSION
    ? t.FROALA.INSERT_TAGS.START_DATE(getLabelForActivity(activityKey, aliases))
    : t.FROALA.INSERT_TAGS.DUE_DATE(getLabelForActivity(activityKey, aliases));
  return label;
};

// Create a list of course activity type dropdown
const createCourseActivityTypeDropDown = (
  courseActivities: CourseActivities,
  rteTagType: RteTagTypes,
  aliases: CourseAliases,
  callback: (type: ActivityKey | LecturePageType) => void,
): NvDropdownTextItem[] => {
  // Add Activities to the DropDown
  let types: NvDropdownTextItem[] = _.keys(courseActivities).map((type: ActivityKey) => ({
    type: 'text',
    text: rteTagType === RteTagTypes.DATE ? getDateActivityDropdownLabel(type, aliases) : getLabelForActivity(type, aliases),
    value: type,
    class: 'rte-tag-dropdown-item',
    callback: () => {
      callback(type);
    },
  }));
  // Add Lecture Page Type to the Types Array
  types = [
    {
      type: 'text',
      text: rteTagType === RteTagTypes.DATE ? t.FROALA.INSERT_TAGS.RELEASE_DATE(aliases.lectureAliases.LectureAlias) : aliases.lectureAliases.LectureAlias,
      value: LecturePageType.LECTURE,
      class: 'rte-tag-dropdown-item',
      callback: () => {
        callback(LecturePageType.LECTURE);
      },
    },
    ...types,
  ];
  return types;
};

// For Due Date RTE Tag, Get activities with due date ( Except for Live Session )
const filterActivitiesWithTagType = (activities: CourseActivities, rteTagType: RteTagTypes): CourseActivities => {
  // If Rte Tag Type is Date
  if (rteTagType === RteTagTypes.DATE) {
    const filteredActivities = {};
    _.map(_.keys(activities), (key: ActivityKey) => {
      const activitiesThatMatched = key === ActivityKey.LIVE_SESSION
        ? activities[key] // IF Live Session- Display Start date Tag
        : _.filter((activities[key] || []), (activity) => (!!activity.dueDate)); // Filter Activities With Due Date
      if (activitiesThatMatched.length) {
        filteredActivities[key] = activitiesThatMatched;
      }
    });
    return filteredActivities;
  }
  return activities;
};

export const FroalaCreateNewTagModal = ({
  onClose,
  onAdd,
  tags,
  rteTagType,
}: {
  onClose(): void,
  onAdd(tags: RteTag): void,
  tags: { [key: string]: RteTag },
  rteTagType: RteTagTypes,
}) => {
  const aliases = useSelector((state) => getCourseAliases(state));
  const courseActivities: CourseActivities = useSelector((state) => getCourseActivities(state));

  const [entityKey, setEntityKey] = useState(null);
  const [entity, setEntity] = useState(null);
  const timeLine: TimelineNormalized[] = useSelector((state) => getCourseTimeLine(state));

  const isDisabled = (entityType: OwnerType, entityId: number, isFeedbackPublic: boolean) => _.has(tags, createRteTagValue(entityType, entityId, rteTagType, isFeedbackPublic));

  // Filter course activities ( Avoid assignments that doesn't have dueDate For Data Type RTE tag)
  const filteredCourseActivities: CourseActivities = useMemo(() => filterActivitiesWithTagType(courseActivities, rteTagType), [courseActivities, rteTagType]);

  // Get Activities Type Drop
  const lectureEntityTypes: NvDropdownTextItem[] = useMemo(() => createCourseActivityTypeDropDown(filteredCourseActivities, rteTagType, aliases, (type) => {
    setEntityKey(type);
    setEntity(null);
  }), [filteredCourseActivities, rteTagType, aliases]);

  const timelineDropdownItems: NvDropdownOption[] = [];
  let lastTimelineItem: TimelineNormalized;
  (timeLine || []).forEach((item) => {
    if (item.type === TimelineItemTypes.PAGE) {
      // Its a Page
      timelineDropdownItems.push({
        type: 'text',
        text: item.title,
        value: item.id,
        disabled: isDisabled(LecturePageType.LECTURE, item.id, false),
        class: 'rte-tag-dropdown-item',
        callback: () => {
          setEntity(item);
        },
      });
    } else {
      // Subsection or Section
      if (lastTimelineItem?.type === TimelineItemTypes.PAGE) {
        // Add a divider if its a section or subsection after a page
        timelineDropdownItems.push({
          type: 'divider',
        });
      }
      timelineDropdownItems.push({
        type: 'header',
        title: item.title,
        class: 'page-title-xxs',
      });
    }
    lastTimelineItem = item;
  });

  const lectureEntities: NvDropdownTextItem[] = entityKey === LecturePageType.LECTURE
    ? timelineDropdownItems
    : (filteredCourseActivities[entityKey] || []).map((item) => ({
      type: 'text',
      text: item.title,
      value: item.id,
      disabled: isDisabled(getActivityTypeForKey(entityKey), item.id, item.isFeedbackPublic),
      class: 'rte-tag-dropdown-item',
      callback: () => {
        setEntity(item);
      },
    }));

  const addNewCustomTag = () => {
    const tag = convertRelatedActivityToRteTag({
      id: entity.id,
      type: entityKey === LecturePageType.LECTURE
        ? LecturePageType.LECTURE
        : getActivityTypeForKey(entityKey),
    }, rteTagType, true, entity.isFeedbackPublic);
    onAdd(tag);
    onClose();
  };

  return (
    <div className='bs4-row' css={styles}>
      <div className='bs4-col-md-3'>
        <NvDropdown
          showSelectedIndicator
          items={lectureEntityTypes}
          buttonStyle={NvDropdownButtonStyle.FORM}
          title={entityKey ? lectureEntityTypes.find(item => item.value === entityKey).text : t.FORM.DROPDOWN.SELECT()}
          titleClass='rte-tag-dropdown-title'
        />
      </div>
      <div className='bs4-col-md-4'>
        <NvDropdown
          showSelectedIndicator
          items={lectureEntities}
          buttonStyle={NvDropdownButtonStyle.FORM}
          title={entity ? lectureEntities.find(item => item.value === entity.id).text : t.FORM.DROPDOWN.SELECT()}
          disabled={entityKey == null}
          titleClass='rte-tag-dropdown-title'
        />
      </div>
      <div className='bs4-col-md-5 add-tags-button-container'>
        <Button
          type='button'
          size='sm'
          disabled={!entity || !entityKey}
          variant='primary'
          onClick={addNewCustomTag}
        >
          {t.FORM.ADD()}
        </Button>
        <Button type='button' size='sm' variant='secondary' onClick={onClose}>{t.FORM.CANCEL()}</Button>
      </div>
    </div>
  );
};

export default FroalaCreateNewTagModal;
